import { useCallback, useEffect, useRef, useState } from "react";
import { EndingPanel } from "@/components/game/EndingPanel";
import { SupporterBadge } from "@/components/SupporterBadge";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pause, Volume2, VolumeX, BookOpen } from "lucide-react";
import { initAudio, setMuted, isMuted, suspendAudio } from "@/game/audio";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ROOMS, getItem, itemLines, CARRIED, ch2Start, ch3Start } from "@/game/rooms";
import { useFlask } from "@/game/useFlask";
import { ProgressCounter } from "@/components/game/ProgressCounter";
import { GameCanvas } from "@/game/GameCanvas";
import { PauseMenu } from "@/components/game/PauseMenu";
import { CutsceneOverlay } from "@/components/game/CutsceneOverlay";
import { InventoryButton } from "@/components/game/InventoryButton";
import { DialogBox, Prompt, Inventory, MobileControls, useKeyboard } from "@/components/game/Hud";

export default function GamePage() {
  const { chapterId } = useParams();
  const nav = useNavigate();
  const [initial, setInitial] = useState(null);
  const [roomId, setRoomId] = useState("hallway");
  const [inventory, setInventory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [prompt, setPrompt] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [cutscene, setCutscene] = useState(null);
  const [runKey, setRunKey] = useState(0);
  const input = useRef({ left: false, right: false, interact: false });
  const pausedRef = useRef(false);
  const menuRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuTab, setMenuTab] = useState("menu");
  const [saving, setSaving] = useState(false);
  const engineRef = useRef(null);
  useKeyboard(input);
  const [tipsy, setTipsy] = useState(false);
  const [endClosed, setEndClosed] = useState(false);
  useFlask({ pausedRef, menuRef, engineRef, onDrink: () => { setTipsy(true); setTimeout(() => setTipsy(false), 4600); } });
  const [muted, setMutedState] = useState(isMuted());

  useEffect(() => {
    const unlock = () => initAudio();
    initAudio();
    window.addEventListener("keydown", unlock); window.addEventListener("pointerdown", unlock);
    return () => { window.removeEventListener("keydown", unlock); window.removeEventListener("pointerdown", unlock); suspendAudio(); };
  }, []);
  useEffect(() => { if (engineRef.current) engineRef.current.bg = dialog?.[0]?.bg || null; }, [dialog]);
  const toggleMute = () => { setMuted(!muted); setMutedState(!muted); };
  const setMenu = (v, tab = "menu") => { menuRef.current = v; setMenuTab(tab); setMenuOpen(v); };

  useEffect(() => {
    const onKey = (e) => {
      if (!engineRef.current || e.target.tagName === "INPUT") return;
      if (e.key === "Escape") { e.preventDefault(); setMenu(!menuRef.current); }
      if (e.key === "j" || e.key === "J") setMenu(!menuRef.current, "journal");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    api.get(`/progress/${chapterId}`).then(async (r) => {
      if (chapterId === "chapter_3" && !(r.data.notes || []).length) r = { data: ch3Start() };
      if (chapterId === "chapter_2" && !(r.data.notes || []).length) {
        const ch1 = await api.get("/progress/chapter_1").then((x) => x.data.notes || []).catch(() => []);
        r = { data: ch2Start(ch1) };
      }
      const inv = r.data.inventory || [];
      const p = { room: ROOMS[r.data.room] ? r.data.room : "hallway", x: r.data.x ?? 330, inventory: chapterId === "chapter_3" ? inv : [...CARRIED.map((c) => c.id).filter((id) => !inv.includes(id)), ...inv], notes: r.data.notes || [] };
      setInitial(p); setRoomId(p.room); setInventory(p.inventory); setNotes(p.notes);
    }).catch((e) => { toast.error(apiError(e)); nav("/"); });
  }, [chapterId, nav, runKey]);

  const save = useCallback(() => {
    const st = engineRef.current;
    if (!st) return Promise.resolve();
    return api.put(`/progress/${chapterId}`, { room: st.room, x: st.x, inventory: st.inventory, notes: st.notes }).catch(() => {});
  }, [chapterId]);

  const saveQuit = async () => {
    setSaving(true); await save(); toast.success("Progress saved"); nav("/");
  };

  useEffect(() => () => save(), [save]);

  const openDialog = (t) => { pausedRef.current = true; setDialog(Array.isArray(t) ? t : [{ text: t }]); };
  const closeDialog = () => setDialog((q) => {
    if (q && q[0].choices) return q;
    if (q && q.length > 1) return q.slice(1);
    pausedRef.current = false; return null;
  });
  const onPickup = (it) => {
    setInventory((inv) => [...inv, it.id]);
    openDialog(it.note ? [{ text: it.intro }, { who: it.name, text: `"${it.desc}"` }] : `Found: ${it.name}. ${it.desc}`);
    save();
  };
  const onRoomChange = (room) => { setRoomId(room); save(); };
  const choose = (c) => {
    const st = engineRef.current;
    if (st && !st.notes.includes(c.note)) { st.notes.push(c.note); onNote(c.note); }
    setDialog(c.next);
  };
  useEffect(() => {
    const onKey = (e) => { const c = dialog?.[0]?.choices; if (c && c[Number(e.key) - 1]) choose(c[Number(e.key) - 1]); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const reread = (lines) => { setMenu(false); openDialog(lines); };
  const readItem = (id) => { const it = getItem(id); if (it) reread(itemLines(it)); };
  const onAllFound = () => toast.success("You have found everyone and everything. Dinner is ready...", { duration: 3500 });
  const onNote = (id) => { setNotes((n) => [...n, id]); toast("Added to journal", { duration: 1500 }); save(); };

  const showEnd = !endClosed && notes.includes({ chapter_2: "living:event:ch2end", chapter_3: "outside:event:final" }[chapterId] || "cave_tunnel:event:end") && !dialog && !cutscene && !menuOpen;
  useEffect(() => { if (showEnd) pausedRef.current = true; }, [showEnd]);

  const restart = async () => {
    await api.delete(`/progress/${chapterId}`);
    engineRef.current = null; pausedRef.current = false; setDialog(null); setMenu(false); setInitial(null); setRunKey((k) => k + 1);
  };

  return (
    <div className="min-h-screen flex flex-col px-3 sm:px-6 py-4 w-full max-w-[1800px] mx-auto" data-testid="game-page">
      <div className="flex items-center justify-between gap-3 mb-4">
        <button className="pixel-btn ghost flex items-center gap-2" onClick={() => { save(); nav("/"); }} data-testid="game-back-button"><ArrowLeft size={14} /> Menu</button>
        <div className="hidden sm:flex items-center gap-3">
          <p className="pixel-text text-lg text-[#7DD3FC] tracking-widest" data-testid="game-chapter-label">{{ chapter_2: "CHAPTER 2 — THE DRUNKEN IDIOT", chapter_3: "CHAPTER 3 — ARRIVAL" }[chapterId] || "CHAPTER 1 — INITIATION"}</p>
          <SupporterBadge />
        </div>
        <div className="flex items-center gap-3">
          <button className="pixel-btn ghost flex items-center gap-2" onClick={toggleMute} data-testid="game-mute-button" aria-label="Toggle sound">
            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}<span className="hidden sm:inline">{muted ? "Sound off" : "Sound on"}</span>
          </button>
          <button className="pixel-btn ghost flex items-center gap-2" onClick={() => setMenu(true, "journal")} data-testid="game-journal-button" aria-label="Journal">
            <BookOpen size={14} /><span className="hidden sm:inline">Journal</span>
          </button>
          <button className="pixel-btn ghost flex items-center gap-2" onClick={() => setMenu(true)} data-testid="game-pause-button"><Pause size={14} /> Pause</button>
        </div>
      </div>
      <div className={`relative mx-auto w-full border-2 border-[#2E3440] shadow-[0_0_60px_rgba(0,0,0,0.9)] scanlines vignette overflow-hidden ${tipsy ? "tipsy" : ""}`} style={{ maxWidth: "max(320px, calc((100vh - 150px) * 1.7778))" }} data-testid="game-viewport">
        {!cutscene && <div className="absolute top-2 left-3 z-10 pixel-text text-lg sm:text-2xl text-[#F3F4F6] bg-black/60 px-2" data-testid="room-name">{dialog?.[0]?.bg === "memory" ? "— A Memory —" : ROOMS[roomId]?.name}</div>}
        {cutscene && <CutsceneOverlay caption={cutscene.caption} onSkip={() => { input.current.tap = true; }} />}
        {initial && (
          <GameCanvas key={runKey} initial={initial} input={input} pausedRef={pausedRef} menuRef={menuRef} engineRef={engineRef}
            onPrompt={setPrompt} onDialog={openDialog} onCloseDialog={closeDialog} onPickup={onPickup} onRoomChange={onRoomChange} onNote={onNote}
            onCutscene={(on, caption) => setCutscene(on ? { caption } : null)} onAllFound={onAllFound} />
        )}
        {!initial && <div className="aspect-video flex items-center justify-center pixel-text text-2xl text-[#9CA3AF]">Loading...</div>}
        {prompt && !dialog && !cutscene && <Prompt label={prompt} />}
        {dialog && <DialogBox line={dialog[0]} more={dialog.length > 1} onClose={closeDialog} onChoose={choose} />}
        {showEnd && <EndingPanel chapter={chapterId} onClose={() => { setEndClosed(true); pausedRef.current = false; }} />}
        {menuOpen && <PauseMenu key={menuTab} initialTab={menuTab} onResume={() => setMenu(false)} onSaveQuit={saveQuit} onRestart={restart} saving={saving} muted={muted} onToggleMute={toggleMute} inventory={inventory} notes={notes} onRead={reread} />}
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-4">
        <InventoryButton items={inventory} onRead={readItem} />
        <ProgressCounter notes={notes} inventory={inventory} />
        <p className="pixel-text text-lg text-[#64748B] hidden lg:block">A/D or ←/→ walk · E / Space interact · J journal · Esc pause</p>
      </div>
      <div className="mt-5 md:hidden"><MobileControls input={input} /></div>
    </div>
  );
}
